import React, { useEffect, useMemo, useState } from "react";
import {
  MessageSquare,
  User,
  CheckSquare,
  Users,
  Megaphone,
  GitBranch,
  StickyNote,
  Clock,
  ArrowRight,
  ChevronRight,
  PlayCircle
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { useNavigate } from "react-router-dom";
import { listarContextoRecente } from "../../services/recentContext";

const MAPA_ICONE = {
  MessageSquare,
  User,
  CheckSquare,
  Users,
  Megaphone,
  GitBranch,
  StickyNote,
  Clock
};

const MAPA_LABEL = {
  ticket: "Atendimento",
  contact: "Cliente",
  task: "Tarefa",
  internalChat: "Conversa interna",
  campaign: "Campanha",
  flow: "Fluxo",
  ticketNote: "Anotação",
  scheduledMessage: "Agendamento"
};

const MAPA_COR = {
  ticket: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  contact: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  task: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  internalChat: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  campaign: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
  flow: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
  ticketNote: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  scheduledMessage: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
};

function formatarTempo(dataISO) {
  try {
    const d = new Date(dataISO);
    const agora = new Date();
    const diffMs = Math.max(0, agora.getTime() - d.getTime());
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "agora mesmo";
    if (diffMin < 60) return `há ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `há ${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    return `há ${diffD}d`;
  } catch {
    return "";
  }
}

export default function ContinueWhereLeftOff({ compact = false, maxItems = 6 }) {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [itens, setItens] = useState([]);

  const buscar = async () => {
    setCarregando(true);
    try {
      const resultado = await listarContextoRecente(maxItems);
      setItens(resultado?.rows || []);
    } catch {
      setItens([]);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    buscar();
    const timer = setInterval(buscar, 90_000);
    return () => clearInterval(timer);
  }, [maxItems]);

  const itensValidos = useMemo(() => {
    return (itens || []).filter(i => i?.contextPath && i?.contextTitle);
  }, [itens]);

  if (!carregando && itensValidos.length === 0) {
    return null;
  }

  return (
    <Card className="w-full max-w-2xl bg-card border-border/60 shadow-sm">
      <CardContent className={`p-4 ${compact ? "p-3" : "sm:p-6"}`}>
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <PlayCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold tracking-tight">
                Continuar de onde parei
              </h3>
              <p className="truncate text-xs text-muted-foreground">
                {carregando
                  ? "Carregando contexto recente…"
                  : itensValidos.length === 1
                  ? "1 item recente para retomar"
                  : `${itensValidos.length} itens recentes para retomar`}
              </p>
            </div>
          </div>
          {itensValidos.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => itensValidos[0]?.contextPath && navigate(itensValidos[0].contextPath)}
              className="shrink-0 gap-1"
            >
              Retomar <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        {carregando && itensValidos.length === 0 ? (
          <ul className="flex flex-col gap-2">
            {[0, 1, 2].map(i => (
              <li
                key={i}
                className="h-14 w-full rounded-xl bg-muted/50 animate-pulse"
              />
            ))}
          </ul>
        ) : (
          <ul className="flex flex-col gap-1 sm:gap-1.5">
            {itensValidos.slice(0, maxItems).map(item => {
              const Icon =
                (item.entityIcon && MAPA_ICONE[item.entityIcon]) ||
                MessageSquare;
              const cor = MAPA_COR[item.entityType] || MAPA_COR.ticket;
              const tag = MAPA_LABEL[item.entityType] || "Contexto";

              return (
                <li key={`${item.entityType}-${item.entityId}`}>
                  <button
                    type="button"
                    onClick={() => navigate(item.contextPath)}
                    className="group flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition hover:bg-muted/60 active:bg-muted/80 sm:p-3"
                  >
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${cor}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium">
                          {item.contextTitle}
                        </p>
                        <Badge
                          variant="outline"
                          className="h-5 shrink-0 truncate border-transparent px-1.5 text-[10px]"
                        >
                          {tag}
                        </Badge>
                      </div>
                      {(item.contextSubtitle || item.lastTouchedAt) && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {item.contextSubtitle && (
                            <span className="truncate">
                              {item.contextSubtitle}
                            </span>
                          )}
                          {item.contextSubtitle && item.lastTouchedAt && (
                            <span className="mx-1 text-border"> · </span>
                          )}
                          {item.lastTouchedAt && (
                            <span className="whitespace-nowrap">
                              {formatarTempo(item.lastTouchedAt)}
                            </span>
                          )}
                        </p>
                      )}
                    </div>

                    <ArrowRight className="ml-1 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition group-hover:opacity-100 group-hover:translate-x-0.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
